import { memo, useCallback } from 'react';

import { type ScopedPermissions, type ShortUrlResponse } from '@air/api/types';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { ShareCommentsPermissions } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/ShareCommentsPermissions';
import { ShareCustomFieldsPermissions } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/ShareCustomFieldsPermissions';
import { ShareSecurityToggles } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/ShareSecurityToggles';
import { ShareVersionsPermissions } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/ShareVersionsPermissions';
import { type UpdateShortIdParams } from '~/swr-hooks/shortUrl/useUpdateShortId';

type SharePrivateContentSettingsProps = {
  onShortUrlUpdate: (
    props: DeepPartial<Omit<UpdateShortIdParams, 'id' | 'objectId' | 'boardContext' | 'shortId'>>,
  ) => Promise<ShareUrlResponseWithSource>;
  isLoading: boolean;
  shortIdInfo: ShortUrlResponse;
  userPermissions: ScopedPermissions;
};

export const SharePrivateContentSettings = memo(
  ({ shortIdInfo, onShortUrlUpdate, isLoading, userPermissions }: SharePrivateContentSettingsProps) => {
    const onPermissionUpdated = useCallback(
      (permissions: DeepPartial<ShortUrlResponse['permissions']>) =>
        onShortUrlUpdate({
          permissions,
        }),
      [onShortUrlUpdate],
    );

    return (
      <div className="flex flex-col gap-8">
        <ShareVersionsPermissions
          isLoading={isLoading}
          shortIdInfo={shortIdInfo}
          onPermissionUpdated={onPermissionUpdated}
          canViewAllVersions={userPermissions.asset.canViewAllVersions}
        />

        <ShareCommentsPermissions
          isLoading={isLoading}
          shortIdInfo={shortIdInfo}
          onPermissionUpdated={onPermissionUpdated}
          canViewDiscussions={userPermissions.asset.canViewDiscussions}
          canManageDiscussions={userPermissions.asset.canManageDiscussions}
        />

        <ShareCustomFieldsPermissions
          isLoading={isLoading}
          shortIdInfo={shortIdInfo}
          onPermissionUpdated={onPermissionUpdated}
          canViewCustomFields={userPermissions.customField.canView}
          canEditCustomFields={userPermissions.customField.canManageApplications}
        />

        <ShareSecurityToggles isLoading={isLoading} shortIdInfo={shortIdInfo} onShortUrlUpdate={onShortUrlUpdate} />
      </div>
    );
  },
);

SharePrivateContentSettings.displayName = 'SharePrivateContentSettings';
